import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import apiClient from '../../lib/apiClient';
import TransactionListModal from './assessment/TransactionListModal';
import { useVendorOverlay } from './hooks/useVendorOverlay';
import { useVendorColors } from './hooks/useVendorColors';
import { useSpendIndicators } from './hooks/useSpendIndicators';
import { buildCoverageMap, enhanceVendor } from '../../lib/vendorCoverage';
import MitreMatrix from './MitreMatrix/MitreMatrix';
import CisaMaturityModel from './CisaMaturityModel/CisaMaturityModel';
import { generateZtaPdf } from './lib/exportZtaPdf';
import { useZtaDepartmentData } from './hooks/useZtaDepartmentData';
import { useZtaSuggestions } from './hooks/useZtaSuggestions';
import { useZtaDrillIn } from './hooks/useZtaDrillIn';
import ZtaHeader from './layers/ZtaHeader';
import ZtaGovernanceLayer from './layers/ZtaGovernanceLayer';
import ZtaSignalsRow from './layers/ZtaSignalsRow';
import ZtaPillars from './layers/ZtaPillars';
import ZtaVisibilityLayer from './layers/ZtaVisibilityLayer';
import ZtaPeopleProcessLayer from './layers/ZtaPeopleProcessLayer';
import ZtaRoadmapLayer from './layers/ZtaRoadmapLayer';
import ZtaModals from './modals/ZtaModals';
import './ZeroTrustArchitecture.css';

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const ZeroTrustArchitecture = ({ selectedDepartment }) => {
  const [editingCapability, setEditingCapability] = useState(null);
  const [editingGovernance, setEditingGovernance] = useState(null);
  const [editingSignal, setEditingSignal] = useState(null);
  const [editingPDP, setEditingPDP] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef(null);

  // Department-scoped assessment data (cache-first) + save paths
  const {
    capabilityData,
    governanceData,
    signalData,
    pdpData,
    setSignalData,
    setPdpData,
    saving,
    saveError,
    clearSaveError,
    isRefreshing,
    saveCapability,
    saveGovernance,
    saveSignal,
    savePDP,
  } = useZtaDepartmentData(selectedDepartment);

  // Smart Suggestions (signals + capabilities) from purchase history
  const {
    suggestions,
    suggestionsLoading,
    fetchSuggestions,
    capabilitySuggestions,
  } = useZtaSuggestions(selectedDepartment);
  const [autoPopulating, setAutoPopulating] = useState(false);
  const [suggestionModeEnabled, setSuggestionModeEnabled] = useState(false);

  // Vendor overlay — multi-select. `coverageMap[capId]` is an array of
  // `{ vendor, productName, coverageLevel, notes }`, sorted strongest first.
  const {
    overlays: vendorOverlays,
    loading: vendorOverlayLoading,
    toggleVendor: toggleOverlayVendor,
    clearVendors: clearOverlayVendors,
  } = useVendorOverlay();
  const coverageMap = useMemo(() => buildCoverageMap(vendorOverlays), [vendorOverlays]);
  const selectedOverlayVendors = useMemo(() => (
    vendorOverlays
      .map(o => (o?.vendor ? enhanceVendor({
        vendor_id: o.vendor.vendor_id,
        vendor_name: o.vendor.vendor_name,
      }) : null))
      .filter(Boolean)
  ), [vendorOverlays]);
  const hasAnyOverlay = selectedOverlayVendors.length > 0;
  const { colorFor: colorForVendor } = useVendorColors();

  // Spend-indicator payload drives the $ drill-in icons next to capability
  // rows and PDP signal boxes. One fetch per department, cached by react-query.
  const { data: spendIndicators } = useSpendIndicators(
    selectedDepartment?.department_id
  );

  // Global $ visibility toggle — useful for presenting without the drill-in
  // noise. Persisted in localStorage so the preference survives refreshes.
  const [showSpendDrills, setShowSpendDrills] = useState(() => {
    if (typeof window === 'undefined') return true;
    try {
      const raw = window.localStorage.getItem('zt-show-spend-drills');
      return raw === null ? true : raw === 'true';
    } catch {
      return true;
    }
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem('zt-show-spend-drills', String(showSpendDrills));
    } catch {
      // storage unavailable — tolerate and keep in-memory state
    }
  }, [showSpendDrills]);

  // View toggle: 'architecture' (default — the full reference architecture)
  // or 'mitre' (the MITRE ATT&CK Matrix tab). The architecture body is
  // swapped for <MitreMatrix> when in mitre mode; the toolbar stays put
  // so the user can flip back without leaving the page.
  const [viewMode, setViewMode] = useState('architecture');

  // Downstream code treats a falsy indicator payload as "no $ icons" —
  // gating here keeps every render site in sync with the toggle without
  // threading a separate boolean through every component.
  const effectiveSpendIndicators = showSpendDrills ? spendIndicators : null;

  // Top-level drill-in — opened by the $ icons; state + fetch routing live
  // in the hook, TransactionListModal renders at the bottom of this tree.
  const {
    drillIn,
    setDrillIn,
    openCapabilityDrillIn,
    openSignalDrillIn,
    openVendorDrillIn,
    fetchDrillInTransactions,
  } = useZtaDrillIn(selectedDepartment, spendIndicators);

  // Editing state for visibility tools
  const [editingVisibilityTool, setEditingVisibilityTool] = useState(null);

  // PDF Export state
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState(null);

  // Present mode handlers (CSS-based, not browser fullscreen)
  const togglePresentMode = useCallback(() => {
    const newState = !isFullscreen;
    setIsFullscreen(newState);
    // Add/remove body class to hide TopNav and Breadcrumbs
    document.body.classList.toggle('zt-present-mode-active', newState);
    // Dispatch custom event for other components
    window.dispatchEvent(new CustomEvent('zt-present-mode', { detail: { active: newState } }));
  }, [isFullscreen]);

  // Handle ESC key to exit present mode
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
        document.body.classList.remove('zt-present-mode-active');
        window.dispatchEvent(new CustomEvent('zt-present-mode', { detail: { active: false } }));
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      // Clean up on unmount
      document.body.classList.remove('zt-present-mode-active');
    };
  }, [isFullscreen]);

  // PDF Export function - generates a professional assessment report
  const exportToPDF = useCallback(async () => {
    if (!containerRef.current || !selectedDepartment) return;

    setIsExporting(true);
    setExportError(null);

    try {
      await generateZtaPdf({ selectedDepartment, capabilityData });
    } catch (error) {
      console.error('Error generating PDF:', error);
      setExportError('Error generating PDF. Please try again.');
    } finally {
      setIsExporting(false);
    }
  }, [selectedDepartment, capabilityData]);

  // Auto-populate signals from suggestions
  const handleAutoPopulate = useCallback(async (signalTypes = null) => {
    if (!selectedDepartment?.department_id) return;

    setAutoPopulating(true);
    try {
      const result = await apiClient.post(
        `/api/zero-trust/departments/${selectedDepartment.department_id}/auto-populate`,
        { signalTypes }
      );

      // Refresh signals after auto-populate
      const sigData = await apiClient.get(
        `/api/zero-trust/departments/${selectedDepartment.department_id}/signals`
      );
      const pdpResult = await apiClient.get(
        `/api/zero-trust/departments/${selectedDepartment.department_id}/pdp`
      );

      const mappedSig = {};
      (sigData.items || []).forEach(s => {
        mappedSig[s.signal_id] = s;
      });

      setSignalData(mappedSig);
      setPdpData(pdpResult.data || {});

      // Refresh suggestions to update their state
      await fetchSuggestions();

      console.log('✅ Auto-populated signals:', result);
      return result;
    } catch (err) {
      console.error('Error auto-populating:', err);
    } finally {
      setAutoPopulating(false);
    }
  }, [selectedDepartment?.department_id, setSignalData, setPdpData, fetchSuggestions]);

  // Get suggestion for a specific signal type
  const getSuggestion = (signalId) => suggestions?.suggestions?.[signalId] || null;

  // Get suggestion for a specific capability
  const getCapabilitySuggestion = (capId) => capabilitySuggestions?.capabilities?.[capId] || null;

  // Check if there are any actionable suggestions
  const hasActionableSuggestions = suggestions?.suggestions &&
    Object.values(suggestions.suggestions).some(s => s.detected?.length > 0 && s.needsUpdate);

  const getCapabilityState = (capId) => capabilityData[capId] || {};
  const getGovernanceState = (govId) => governanceData[govId] || {};
  const getSignalState = (sigId) => signalData[sigId] || {};

  return (
    <div
      className={`zta ${isFullscreen ? 'zta--fullscreen' : ''} ${hasAnyOverlay ? 'zta--has-overlay' : ''}`}
      ref={containerRef}
      style={
        selectedOverlayVendors.length === 1
          ? { '--overlay-color': colorForVendor(selectedOverlayVendors[0]) }
          : undefined
      }
    >
      {/* Header */}
      <ZtaHeader
        selectedDepartment={selectedDepartment}
        suggestionModeEnabled={suggestionModeEnabled}
        setSuggestionModeEnabled={setSuggestionModeEnabled}
        showSpendDrills={showSpendDrills}
        setShowSpendDrills={setShowSpendDrills}
        hasActionableSuggestions={hasActionableSuggestions}
        handleAutoPopulate={handleAutoPopulate}
        autoPopulating={autoPopulating}
        suggestionsLoading={suggestionsLoading}
        isRefreshing={isRefreshing}
        selectedOverlayVendors={selectedOverlayVendors}
        toggleOverlayVendor={toggleOverlayVendor}
        clearOverlayVendors={clearOverlayVendors}
        vendorOverlayLoading={vendorOverlayLoading}
        hasAnyOverlay={hasAnyOverlay}
        exportToPDF={exportToPDF}
        isExporting={isExporting}
        togglePresentMode={togglePresentMode}
        isFullscreen={isFullscreen}
        viewMode={viewMode}
        setViewMode={setViewMode}
      />

      {/* Inline failure notices — a failed save/export must never be silent
          (alerts are banned). Saves are optimistic; on failure the change is
          reverted and explained here. */}
      {saveError && (
        <div className="zta-inline-error" role="alert">
          <span>{saveError.message}</span>
          <button type="button" className="zta-inline-error-dismiss" onClick={clearSaveError}>
            Dismiss
          </button>
        </div>
      )}
      {exportError && (
        <div className="zta-inline-error" role="alert">
          <span>{exportError}</span>
          <button type="button" className="zta-inline-error-dismiss" onClick={() => setExportError(null)}>
            Dismiss
          </button>
        </div>
      )}

      {viewMode === 'mitre' ? (
        <div className="zta-mitre-wrap">
          <MitreMatrix selectedDepartment={selectedDepartment} />
        </div>
      ) : viewMode === 'cisa' ? (
        <div className="zta-cisa-wrap">
          <CisaMaturityModel selectedDepartment={selectedDepartment} />
        </div>
      ) : (<>
      {/* Governance Layer */}
      <ZtaGovernanceLayer
        getGovernanceState={getGovernanceState}
        setEditingGovernance={setEditingGovernance}
      />

      {/* Policy Decision Layer */}
      <ZtaSignalsRow
        selectedDepartment={selectedDepartment}
        getSignalState={getSignalState}
        getSuggestion={getSuggestion}
        suggestionModeEnabled={suggestionModeEnabled}
        coverageMap={coverageMap}
        effectiveSpendIndicators={effectiveSpendIndicators}
        pdpData={pdpData}
        setEditingSignal={setEditingSignal}
        setEditingPDP={setEditingPDP}
        openSignalDrillIn={openSignalDrillIn}
        openVendorDrillIn={openVendorDrillIn}
      />

      {/* Policy Enforcement - 6 Pillars */}
      <ZtaPillars
        selectedDepartment={selectedDepartment}
        getCapabilityState={getCapabilityState}
        setEditingCapability={setEditingCapability}
        saveCapability={saveCapability}
        getCapabilitySuggestion={getCapabilitySuggestion}
        suggestionModeEnabled={suggestionModeEnabled}
        coverageMap={coverageMap}
        effectiveSpendIndicators={effectiveSpendIndicators}
        openCapabilityDrillIn={openCapabilityDrillIn}
        openVendorDrillIn={openVendorDrillIn}
      />

      {/* Visibility & Analytics - Cross-cutting capabilities spanning all 5 pillars */}
      <ZtaVisibilityLayer
        selectedDepartment={selectedDepartment}
        getCapabilityState={getCapabilityState}
        getCapabilitySuggestion={getCapabilitySuggestion}
        suggestionModeEnabled={suggestionModeEnabled}
        coverageMap={coverageMap}
        effectiveSpendIndicators={effectiveSpendIndicators}
        setEditingVisibilityTool={setEditingVisibilityTool}
        openVendorDrillIn={openVendorDrillIn}
      />

      {/* People & Process */}
      <ZtaPeopleProcessLayer />

      {/* Roadmap */}
      <ZtaRoadmapLayer />
      </>)}

      {/* Edit modals — capability, governance, signal, PDP, visibility tool */}
      <ZtaModals
        selectedDepartment={selectedDepartment}
        suggestionModeEnabled={suggestionModeEnabled}
        editingCapability={editingCapability}
        setEditingCapability={setEditingCapability}
        editingGovernance={editingGovernance}
        setEditingGovernance={setEditingGovernance}
        editingSignal={editingSignal}
        setEditingSignal={setEditingSignal}
        editingPDP={editingPDP}
        setEditingPDP={setEditingPDP}
        editingVisibilityTool={editingVisibilityTool}
        setEditingVisibilityTool={setEditingVisibilityTool}
        pdpData={pdpData}
        saving={saving}
        getCapabilityState={getCapabilityState}
        getGovernanceState={getGovernanceState}
        getSignalState={getSignalState}
        getSuggestion={getSuggestion}
        getCapabilitySuggestion={getCapabilitySuggestion}
        saveCapability={saveCapability}
        saveGovernance={saveGovernance}
        saveSignal={saveSignal}
        savePDP={savePDP}
      />

      {/* No Department Selected Overlay */}
      {!selectedDepartment && (
        <div className="zta-no-dept-hint">
          Select a department to assign vendors and track maturity
        </div>
      )}

      {/* Top-level spend drill-in — opened by the $ icons on capabilities,
          PDP signals, and the PDP center box. Routes to the capability or
          signal transactions endpoint based on drillIn.kind. */}
      <TransactionListModal
        isOpen={!!drillIn}
        onClose={() => setDrillIn(null)}
        capabilityCode={drillIn?.code}
        capabilityName={drillIn?.name}
        vendor={drillIn?.vendor}
        product={drillIn?.product}
        fetchTransactions={fetchDrillInTransactions}
      />
    </div>
  );
};

export default ZeroTrustArchitecture;
